import { useCallback, useState } from 'react'
import { LIMITS } from '@weblite/permission'
import { useCountDownToDate } from '@weblite/logic-toolkit'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import {
  resendCode,
  VerificationSession,
  VerifyError,
  verifyPhone,
} from '$logic/auth'
import { t } from '$i18n'
import { nothing } from '$func/utils'

const ERROR_MAPPER: Record<VerifyError, string> = {
  WRONG_CODE: t('signin.verification.wrongCodeEntered'),
  SERVER_ERROR: t('serverError'),
  CLOSE_VERIFICATION: '',
}

const calculateEndDate = () =>
  LIMITS.verificationCode.resendInSeconds * 1000 + dateWithOffset()

export function useVerifyPhoneForm({
  phone,
  sessionId,
  signInUser,
  onVerified,
  onCloseVerification = nothing,
}: VerificationSession & {
  signInUser?: boolean
  onVerified?: (token: string) => void
  onCloseVerification?: () => unknown
}) {
  const [loading, setLoading] = useState(false)
  const [resendCount, setResendCount] = useState(0)
  const [alertText, setAlertText] = useState('')

  const [endDate, setEndDate] = useState(calculateEndDate())

  const remainingMillis = useCountDownToDate(endDate, dateWithOffset)

  const resendMaxed = resendCount >= LIMITS.verificationCode.maxResends

  const handleResendCode = useCallback(async () => {
    setLoading(true)
    try {
      if (await resendCode(phone, sessionId, onCloseVerification)) {
        setEndDate(calculateEndDate())
        setResendCount(count => count + 1)
      }
    } finally {
      setLoading(false)
    }
  }, [phone, sessionId])

  const handleCodeComplete = useCallback(
    async (code: string) => {
      setLoading(true)
      try {
        const res = await verifyPhone(phone, sessionId, code, signInUser)
        if (typeof res !== 'string') onVerified?.(res.token)
        else if (res === 'CLOSE_VERIFICATION') onCloseVerification()
        else setAlertText(ERROR_MAPPER[res])
      } finally {
        setLoading(false)
      }
    },
    [phone, sessionId, onVerified, signInUser, onCloseVerification],
  )

  const handleCodeChange = useCallback(
    async (code: string) => {
      if (code.length === LIMITS.verificationCode.length) {
        await handleCodeComplete(code)
      } else setAlertText('')
    },
    [handleCodeComplete],
  )

  return {
    handleCodeChange,
    handleResendCode,
    resendMaxed,
    remainingMillis,
    alertText,
    loading,
  }
}
